/* eslint-disable no-plusplus */
// @mui material components
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React context
import { useMaterialUIController } from "context";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import OrchardListEdit from "examples/Lists/OrchardListEdit";
import { useLocation } from "react-router-dom";

// React
import { useEffect, useState } from "react";

// firebase functions
import { db } from "firebaseDb";
import { collection, doc, getDocs, query, where, writeBatch } from "firebase/firestore";
import normalizeSiteCodeAcl from "functions/normalizeSiteCodeAcl";

function Chemical() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { state } = useLocation();
  const [orchardInfo, setOrchardInfo] = useState({
    ...state.site,
    ownerUIDs: Array.isArray(state.site.ownerUIDs) ? state.site.ownerUIDs : [],
  });
  const [ownerUsers, setOwnerUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadOwners = async () => {
      const usersRef = collection(db, "Users");
      const ownersSnapshot = await getDocs(query(usersRef, where("permissions", "==", "Owner")));
      setOwnerUsers(
        ownersSnapshot.docs.map((ownerDoc) => ({
          uid: ownerDoc.id,
          name: ownerDoc.data().name || ownerDoc.data().displayName || ownerDoc.id,
        }))
      );
    };

    loadOwners().catch((error) => {
      console.error("Error loading owner users:", error);
    });
  }, []);
  const saveOrchard = () => {
    const batch = writeBatch(db);
    setLoading(true);

    try {
      for (let i = 0; i < orchardInfo.blocks.length; i++) {
        for (let j = 0; j < orchardInfo.blocks[i].varieties.length; j++) {
          const variety = orchardInfo.blocks[i].varieties[j];

          if (
            !variety.key ||
            !orchardInfo.orchardGUID ||
            !orchardInfo.cropSureID ||
            !orchardInfo.blocks[i].cropSureBlockID
          ) {
            throw new Error(`Missing required fields for variety at index (${i},${j})`);
          }

          const orcRef = doc(db, "Orchards", variety.key);

          const block = orchardInfo.blocks[i];
          let blockNupointBlockID = block.nupointBlockID || "";

          if (!blockNupointBlockID && Array.isArray(block.varieties)) {
            const matchingVariety = block.varieties.find(
              (currentVariety) => currentVariety.nupointBlockID
            );

            if (matchingVariety) {
              blockNupointBlockID = matchingVariety.nupointBlockID;
            }
          }
          const payload = {
            ...variety,
            orchardGUID: orchardInfo.orchardGUID,
            cropSureID: orchardInfo.cropSureID,
            cropSureBlockID: block.cropSureBlockID,
            blockName: block.blockName,
            blockCode: block.blockCode,
            ownerUIDs: Array.isArray(orchardInfo.ownerUIDs) ? orchardInfo.ownerUIDs : [],
            ...(blockNupointBlockID ? { nupointBlockID: blockNupointBlockID } : {}),
          };

          // Queue the set operation in the batch
          batch.set(orcRef, payload, { merge: true });
        }
      }

      // Commit the batch to Firestore
      batch
        .commit()
        .then(() => {
          console.log("Batch write completed successfully.");
          setLoading(false);
          alert("Saved successfully.");
        })
        .catch((error) => {
          alert("Error performing batch write");
          console.error(error);
          setLoading(false);
        });
    } catch (error) {
      alert("Error preparing batch write:", error);
      console.error(error);
      setLoading(false);
    }
  };

  const archiveOrchard = () => {
    const batch = writeBatch(db);
    setLoading(true);

    try {
      for (let i = 0; i < orchardInfo.blocks.length; i++) {
        for (let j = 0; j < orchardInfo.blocks[i].varieties.length; j++) {
          const variety = orchardInfo.blocks[i].varieties[j];

          const orcRef = doc(db, "Orchards", variety.key);

          // Queue the set operation in the batch
          batch.set(
            orcRef,
            {
              archive: true,
            },
            { merge: true }
          );
        }
      }

      // Commit the batch to Firestore
      batch
        .commit()
        .then(() => {
          console.log("Batch write completed successfully.");
          setLoading(false);
          alert("Orchard archived successfully.");
        })
        .catch((error) => {
          alert("Error performing batch write");
          console.error(error);
          setLoading(false);
        });
    } catch (error) {
      alert("Error preparing batch write:", error);
      console.error(error);
      setLoading(false);
    }
  };

  const applyOwnersToExistingSprays = async () => {
    setLoading(true);
    try {
      const normalizedSiteCode = normalizeSiteCodeAcl(orchardInfo.siteCode);
      const rawSiteCode =
        typeof orchardInfo.siteCode === "string" ? orchardInfo.siteCode.trim() : "";
      const spraySnapshots = await Promise.all(
        [...new Set([normalizedSiteCode, rawSiteCode].filter(Boolean))].map((siteCode) =>
          getDocs(query(collection(db, "Sprays"), where("siteCode", "==", siteCode)))
        )
      );
      const sprayDocsById = new Map();
      spraySnapshots.forEach((snapshot) => {
        snapshot.forEach((sprayDoc) => {
          sprayDocsById.set(sprayDoc.id, sprayDoc);
        });
      });
      const sprayDocs = Array.from(sprayDocsById.values());
      const ownerUIDs = Array.isArray(orchardInfo.ownerUIDs) ? orchardInfo.ownerUIDs : [];
      const chunkStartIndexes = Array.from(
        { length: Math.ceil(sprayDocs.length / 400) },
        (_, index) => index * 400
      );

      await chunkStartIndexes.reduce(async (previousCommit, startIndex) => {
        await previousCommit;
        const batch = writeBatch(db);
        sprayDocs.slice(startIndex, startIndex + 400).forEach((sprayDoc) => {
          const sprayRef = doc(db, "Sprays", sprayDoc.id);
          batch.set(
            sprayRef,
            {
              siteCodeAcl: normalizeSiteCodeAcl(sprayDoc.data().siteCode || orchardInfo.siteCode),
              ownerUIDs,
            },
            { merge: true }
          );
        });
        return batch.commit();
      }, Promise.resolve());
      setLoading(false);
      alert("Owner access applied to existing sprays.");
    } catch (error) {
      console.error("Error applying owner access to sprays:", error);
      alert("Error applying owner access to sprays.");
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar absolute isMini />
      <MDBox
        component="li"
        display="flex"
        justifyContent="center"
        alignItems="flex-start"
        bgColor={darkMode ? "transparent" : "grey-100"}
        borderRadius="lg"
        p={3}
        mb={1}
        mt={10}
      >
        {!loading && (
          <MDBox width="100%" display="flex" flexDirection="column">
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems={{ xs: "flex-start", sm: "center" }}
              flexDirection={{ xs: "column", sm: "row" }}
              mb={2}
            >
              <MDTypography
                variant="caption"
                color="text"
                mr={2}
                sx={{
                  fontSize: "1rem",
                  verticalAlign: "center",
                  left: "30px",
                  position: "relative",
                }}
              >
                Orchard:
              </MDTypography>
              <TextField
                sx={{ left: "38px", position: "relative" }}
                value={orchardInfo.siteName}
                onChange={(e) => {
                  setOrchardInfo((prevState) => ({
                    ...prevState,
                    siteName: e.target.value,
                  }));
                }}
              />
            </MDBox>
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems={{ xs: "flex-start", sm: "center" }}
              flexDirection={{ xs: "column", sm: "row" }}
              mb={2}
            >
              <MDTypography
                variant="caption"
                color="text"
                sx={{
                  fontSize: "1rem",
                  verticalAlign: "center",
                  left: "30px",
                  position: "relative",
                }}
              >
                Spray Owners:
              </MDTypography>
              <Select
                multiple
                displayEmpty
                value={Array.isArray(orchardInfo.ownerUIDs) ? orchardInfo.ownerUIDs : []}
                onChange={(event) =>
                  setOrchardInfo((prevState) => ({
                    ...prevState,
                    ownerUIDs: event.target.value,
                  }))
                }
                renderValue={(selected) => {
                  if (!selected.length) return "Select owners";
                  return selected
                    .map((uid) => ownerUsers.find((owner) => owner.uid === uid)?.name || uid)
                    .join(", ");
                }}
                sx={{ left: "40px", position: "relative", minWidth: 320 }}
              >
                {ownerUsers.map((owner) => (
                  <MenuItem key={owner.uid} value={owner.uid}>
                    <Checkbox checked={(orchardInfo.ownerUIDs || []).indexOf(owner.uid) > -1} />
                    <ListItemText primary={owner.name} secondary={owner.uid} />
                  </MenuItem>
                ))}
              </Select>
            </MDBox>
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems={{ xs: "flex-start", sm: "center" }}
              flexDirection={{ xs: "column", sm: "row" }}
              mb={2}
            >
              <MDTypography
                variant="caption"
                color="text"
                mr={2}
                sx={{
                  fontSize: "1rem",
                  verticalAlign: "center",
                  position: "relative",
                }}
              >
                Retroactively apply spray access:
              </MDTypography>
              <MDButton
                variant="contained"
                color="info"
                disabled={loading}
                onClick={() => {
                  const confirmApply = window.confirm(
                    "Apply selected owners to all existing sprays for this orchard?"
                  );
                  if (confirmApply) {
                    applyOwnersToExistingSprays();
                  }
                }}
              >
                Apply to Existing Sprays
              </MDButton>
            </MDBox>
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems={{ xs: "flex-start", sm: "center" }}
              flexDirection={{ xs: "column", sm: "row" }}
              mb={2}
            >
              <MDTypography
                variant="caption"
                color="text"
                mr={2}
                sx={{
                  fontSize: "1rem",
                  verticalAlign: "center",
                  position: "relative",
                }}
              >
                Archive Orchard:
              </MDTypography>
              <MDButton
                variant="contained"
                color="error"
                disabled={loading}
                onClick={() => {
                  const confirmDelete = window.confirm(
                    "Are you sure you want to delete this orchard?"
                  );
                  if (confirmDelete) {
                    archiveOrchard();
                  }
                }}
              >
                Delete Orchard
              </MDButton>
            </MDBox>

            <MDBox
              display="flex"
              justifyContent="center"
              alignItems={{ xs: "flex-start", sm: "center" }}
              flexDirection={{ xs: "column", sm: "row" }}
              mb={2}
            >
              <MDTypography
                variant="caption"
                color="text"
                mr={2}
                sx={{
                  fontSize: "1rem",
                  verticalAlign: "center",
                  left: "30px",
                  position: "relative",
                }}
              >
                OrchardGUID:
              </MDTypography>
              <TextField
                sx={{ left: "38px", position: "relative" }}
                value={orchardInfo.orchardGUID}
                onChange={(e) => {
                  setOrchardInfo((prevState) => ({
                    ...prevState,
                    orchardGUID: e.target.value,
                  }));
                }}
              />
            </MDBox>
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems={{ xs: "flex-start", sm: "center" }}
              flexDirection={{ xs: "column", sm: "row" }}
              mb={2}
            >
              <MDTypography
                variant="caption"
                color="text"
                mr={2}
                sx={{
                  fontSize: "1rem",
                  verticalAlign: "center",
                  left: "30px",
                  position: "relative",
                }}
              >
                CropSure ID:
              </MDTypography>
              <TextField
                sx={{ left: "38px", position: "relative" }}
                value={orchardInfo.cropSureID}
                onChange={(e) => {
                  setOrchardInfo((prevState) => ({
                    ...prevState,
                    cropSureID: e.target.value,
                  }));
                }}
              />
            </MDBox>
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems={{ xs: "flex-start", sm: "center" }}
              flexDirection={{ xs: "column", sm: "row" }}
              mb={2}
            >
              <MDTypography
                variant="caption"
                color="text"
                sx={{
                  fontSize: "1rem",
                  verticalAlign: "center",
                  left: "30px",
                  position: "relative",
                }}
              >
                Site Code:
              </MDTypography>
              <TextField
                sx={{
                  fontSize: "1rem",
                  verticalAlign: "center",
                  left: "40px",
                  position: "relative",
                }}
                type="text"
                value={orchardInfo.siteCode}
                onChange={(e) => {
                  setOrchardInfo((prevState) => ({
                    ...prevState,
                    siteCode: e.target.value,
                  }));
                }}
              />
            </MDBox>
            <MDBox>
              {orchardInfo && (
                <OrchardListEdit
                  title="Block List"
                  orchardInfo={orchardInfo}
                  setOrchardInfo={setOrchardInfo}
                  varieties={state.varieties}
                />
              )}
            </MDBox>
          </MDBox>
        )}
        {loading && <CircularProgress />}
      </MDBox>
      <MDBox mb={1} lineHeight={0} textAlign="center">
        <MDButton
          variant="contained"
          color="success"
          disabled={loading}
          onClick={() => {
            saveOrchard();
          }}
        >
          <Icon>save</Icon>&nbsp;save
        </MDButton>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Chemical;
